import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import { invalidateApprovalQueries } from "@/hooks/useApproval";
import {
  createScheduleEvent,
  createPerson,
  deactivatePerson,
  deleteScheduleEvent,
  getPeopleOrganization,
  getPeopleAvailability,
  getPerson,
  listPeople,
  listPersonEventTypes,
  patchPerson,
  putPersonWorkTypeIds,
  reactivatePerson,
  resetPersonPassword,
  resetPersonPin,
  updateScheduleEvent,
  type PeopleFilters,
  type PeopleAvailability,
  type PeopleAvailabilityParams,
  type PeopleOrganization,
  type PersonCreateRequest,
  type PersonCreateResponse,
  type PersonPatchRequest,
  type PersonResetPasswordResponse,
  type PersonEventType,
  type PersonRead,
  type PersonScheduleEvent,
  type PersonSchedulePayload,
  type PersonScheduleView,
  type User,
} from "@/api/people";

export const PEOPLE_KEY = ["people"] as const;
export const PEOPLE_ORGANIZATION_KEY = ["people-organization"] as const;
export const PERSON_KEY = (id: number) => ["person", id] as const;
export const PEOPLE_AVAILABILITY_KEY = ["people-availability"] as const;

function invalidatePeopleProjections(qc: ReturnType<typeof useQueryClient>, personId?: number) {
  void qc.invalidateQueries({ queryKey: PEOPLE_KEY });
  void qc.invalidateQueries({ queryKey: PEOPLE_ORGANIZATION_KEY });
  void qc.invalidateQueries({ queryKey: PEOPLE_AVAILABILITY_KEY });
  void qc.invalidateQueries({ queryKey: ["unit"] });
  if (personId != null) void qc.invalidateQueries({ queryKey: PERSON_KEY(personId) });
}

function noRetryOnAuth(failureCount: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
  return failureCount < 1;
}

export function usePeople(filters: PeopleFilters) {
  return useQuery<PersonRead[], Error>({
    queryKey: [...PEOPLE_KEY, filters],
    queryFn: () => listPeople(filters),
    retry: noRetryOnAuth,
    staleTime: 30_000,
  });
}

export function usePeopleOrganization() {
  return useQuery<PeopleOrganization, Error>({
    queryKey: PEOPLE_ORGANIZATION_KEY,
    queryFn: getPeopleOrganization,
    retry: noRetryOnAuth,
    staleTime: 30_000,
  });
}

export function usePeopleAvailability(params: PeopleAvailabilityParams, enabled = true) {
  return useQuery<PeopleAvailability, Error>({
    queryKey: [...PEOPLE_AVAILABILITY_KEY, params],
    queryFn: () => getPeopleAvailability(params),
    retry: noRetryOnAuth,
    staleTime: 30_000,
    enabled,
  });
}

export function usePerson(id: number) {
  return useQuery<PersonScheduleView, Error>({
    queryKey: PERSON_KEY(id),
    queryFn: () => getPerson(id),
    enabled: id > 0,
    retry: noRetryOnAuth,
    staleTime: 30_000,
  });
}

export function useCreatePerson() {
  const qc = useQueryClient();
  return useMutation<PersonCreateResponse, Error, PersonCreateRequest>({
    mutationFn: createPerson,
    onSuccess: (data) => invalidatePeopleProjections(qc, data.user.id),
  });
}

export interface PatchPersonVars {
  id: number;
  body: PersonPatchRequest;
}

export function usePatchPerson() {
  const qc = useQueryClient();
  return useMutation<User, Error, PatchPersonVars>({
    mutationFn: ({ id, body }) => patchPerson(id, body),
    onSuccess: (_data, vars) => {
      invalidatePeopleProjections(qc, vars.id);
    },
  });
}

export function useDeactivatePerson() {
  const qc = useQueryClient();
  return useMutation<User, Error, number>({
    mutationFn: deactivatePerson,
    onSuccess: (_data, id) => {
      invalidatePeopleProjections(qc, id);
    },
  });
}

export function useReactivatePerson() {
  const qc = useQueryClient();
  return useMutation<User, Error, number>({
    mutationFn: reactivatePerson,
    onSuccess: (_data, id) => {
      invalidatePeopleProjections(qc, id);
    },
  });
}

export interface ResetPersonPinVars {
  id: number;
  pin: string;
}

export function useResetPersonPin() {
  const qc = useQueryClient();
  return useMutation<User, Error, ResetPersonPinVars>({
    mutationFn: ({ id, pin }) => resetPersonPin(id, pin),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PEOPLE_KEY });
      void qc.invalidateQueries({ queryKey: PERSON_KEY(vars.id) });
    },
  });
}

export function useResetPersonPassword() {
  const qc = useQueryClient();
  return useMutation<PersonResetPasswordResponse, Error, number>({
    mutationFn: resetPersonPassword,
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: PEOPLE_KEY });
      void qc.invalidateQueries({ queryKey: PERSON_KEY(id) });
    },
  });
}

export function usePersonEventTypes() {
  return useQuery<PersonEventType[], Error>({
    queryKey: ["person-event-types"],
    queryFn: listPersonEventTypes,
    retry: noRetryOnAuth,
    staleTime: 5 * 60_000,
  });
}

export function useCreateScheduleEvent() {
  const qc = useQueryClient();
  return useMutation<PersonScheduleEvent, Error, { userId: number; body: PersonSchedulePayload }>({
    mutationFn: ({ userId, body }) => createScheduleEvent(userId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PERSON_KEY(vars.userId) });
      void qc.invalidateQueries({ queryKey: ["unit-capacity"] });
      void qc.invalidateQueries({ queryKey: PEOPLE_AVAILABILITY_KEY });
      invalidateApprovalQueries(qc);
    },
  });
}

export function useUpdateScheduleEvent() {
  const qc = useQueryClient();
  return useMutation<
    PersonScheduleEvent,
    Error,
    { userId: number; eventId: number; body: PersonSchedulePayload }
  >({
    mutationFn: ({ userId, eventId, body }) => updateScheduleEvent(userId, eventId, body),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PERSON_KEY(vars.userId) });
      void qc.invalidateQueries({ queryKey: PEOPLE_AVAILABILITY_KEY });
      invalidateApprovalQueries(qc);
    },
  });
}

export function useSetPersonWorkTypes() {
  const qc = useQueryClient();
  return useMutation<number[], Error, { userId: number; ids: number[] }>({
    mutationFn: ({ userId, ids }) => putPersonWorkTypeIds(userId, ids),
    onSuccess: (_data, vars) => void qc.invalidateQueries({ queryKey: PERSON_KEY(vars.userId) }),
  });
}

export function useDeleteScheduleEvent() {
  const qc = useQueryClient();
  return useMutation<void, Error, { userId: number; eventId: number }>({
    mutationFn: ({ userId, eventId }) => deleteScheduleEvent(userId, eventId),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: PERSON_KEY(vars.userId) });
      void qc.invalidateQueries({ queryKey: ["unit-capacity"] });
      void qc.invalidateQueries({ queryKey: PEOPLE_AVAILABILITY_KEY });
      invalidateApprovalQueries(qc);
    },
  });
}
